import type React from 'react'
import {useState, useEffect, useImperativeHandle, useRef} from 'react'
import {Textarea, FormControl, Button, Link, Tooltip, useFocusZone} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {FocusKeys} from '@primer/behaviors'
import {
  CopyIcon,
  CheckIcon,
  ThumbsdownIcon,
  ThumbsupIcon,
  SmileyFrustratedIcon,
  SmileyFrownIcon,
  SmileyGrinIcon,
  SmileyIcon,
  SmileyNeutralIcon,
} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import styles from './UserFeedback.module.css'
import colorStyles from './UserFeedbackColors.module.css'
import {clsx} from 'clsx'

const RADIO_SELECTOR = '[role="radio"]'

export type RatingOption<Value> = {
  name: string
  value: Value
  icon: React.ReactNode
  color: keyof typeof colorStyles
}

export interface FeedbackRef<Value> {
  openDialog: (initialOptionValue?: Value) => void
}

export type UserFeedbackProps<Value> = {
  /**
   * Optional title for the dialog.
   * The default is 'Give feedback'.
   */
  title?: string
  requestID?: string
  options: Array<RatingOption<Value>>
  returnFocusRef?: React.RefObject<HTMLElement | null>
  /**
   * Optional placeholder text for the message textarea.
   * The default is 'Tell us what you liked or what could be better'.
   */
  placeholderText?: string
  /**
   * Whether selecting a rating is required. Controls the `aria-required` state
   * announced for the rating radio group. Defaults to `true` because every
   * consumer validates that a rating was selected before submitting.
   */
  required?: boolean
  /**
   * Called when the user submits the form. Returns an array of errors, which will be displayed to the user.
   * @param ratingValue The value of the selected rating option, or null if no option is selected.
   * @param feedbackText The text of the feedback.
   * @returns An array of errors, which will be displayed to the user.
   */
  onSubmit: (ratingValue: Value | null, feedbackText: string) => Promise<string[]>
  /**
   * Called when the dialog closes. The reason indicates how the dialog was closed:
   * - 'submit': User clicked the Send button and onSubmit completed successfully with no errors
   * - 'cancel': User clicked the Cancel button to explicitly dismiss without submitting
   * - 'dismiss': User dismissed the dialog via the X button or Escape key
   */
  onClose?: (reason: 'submit' | 'cancel' | 'dismiss') => void
  ref?: React.Ref<FeedbackRef<Value>>
}

export function UserFeedback<Value>({
  title = 'Give feedback',
  requestID,
  options,
  returnFocusRef,
  placeholderText = 'Tell us what you liked or what could be better',
  required = true,
  onSubmit,
  onClose,
  ref,
}: UserFeedbackProps<Value>) {
  const [selectedRating, setSelectedRating] = useState<Value | null>(null)
  const [feedbackText, setFeedbackText] = useState<string>('')
  const [errors, setErrors] = useState<string[]>([])
  const [errorKey, setErrorKey] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [copiedLogs, setCopiedLogs] = useState(false)
  const ratingGroupRef = useRef<HTMLDivElement>(null)

  useFocusZone(
    {
      containerRef: ratingGroupRef,
      bindKeys: FocusKeys.ArrowAll | FocusKeys.HomeAndEnd,
      // Arrow keys wrap at the ends (WAI-ARIA radio group); Tab still exits the group.
      focusOutBehavior: 'wrap',
      // Tabbing in lands on the checked radio, or the first when none is selected.
      focusInStrategy: () => {
        const group = ratingGroupRef.current
        if (!group) return undefined
        return (
          group.querySelector<HTMLElement>(`${RADIO_SELECTOR}[aria-checked="true"]`) ??
          group.querySelector<HTMLElement>(RADIO_SELECTOR) ??
          undefined
        )
      },
    },
    [options, isOpen],
  )

  useEffect(() => {
    if (errors.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setErrorKey(prev => prev + 1)
    }
  }, [errors])

  useImperativeHandle(ref, () => ({
    openDialog: (initialOptionValue?: Value) => {
      if (initialOptionValue != null) {
        if (!options.some(option => option.value === initialOptionValue)) {
          throw new Error('invalid initial option value')
        }

        setSelectedRating(initialOptionValue)
      }
      setIsOpen(true)
    },
  }))

  const resetForm = () => {
    setSelectedRating(null)
    setFeedbackText('')
    setErrors([])
  }

  const closeDialog = (reason: 'submit' | 'cancel' | 'dismiss') => {
    resetForm()
    setIsOpen(false)
    onClose?.(reason)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    let newErrors = []
    try {
      newErrors = await onSubmit(selectedRating, feedbackText)
    } catch {
      newErrors = ['An error occurred while submitting your feedback.']
    }

    setErrors(newErrors)
    if (newErrors.length === 0) {
      closeDialog('submit')
    }
  }

  const handleDialogClose = () => closeDialog('dismiss')
  const handleCancelClick = () => closeDialog('cancel')

  const handleRatingSelect = (rating: Value) => {
    setSelectedRating(rating)
    setErrors([])
  }

  const handleRatingFocus = (rating: Value, event: React.FocusEvent<HTMLButtonElement>) => {
    // Selection follows focus (WAI-ARIA radio group): check a radio when arrow-key
    // navigation moves focus to it from another radio in the group. Focus arriving from
    // outside the group (initial tab-in) is ignored so the group isn't auto-selected.
    const previouslyFocused = event.relatedTarget
    if (previouslyFocused instanceof Node && ratingGroupRef.current?.contains(previouslyFocused)) {
      handleRatingSelect(rating)
    }
  }

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedbackText(e.target.value)
    setErrors([])
  }

  const handleCopyLogs = async () => {
    if (!requestID) {
      return
    }
    try {
      await navigator.clipboard.writeText(`Request ID: ${requestID}`)
    } catch {
      setErrors(prevErrors => [...prevErrors, 'Failed to copy logs.'])
      return
    }
    setCopiedLogs(true)
    announce('Copied!')
    setTimeout(() => {
      setCopiedLogs(false)
    }, 1000)
  }

  const renderBody = () => (
    <div className={styles.bodyContainer}>
      <div
        ref={ratingGroupRef}
        className={styles.ratingContainer}
        role="radiogroup"
        aria-label="Rating"
        aria-required={required}
      >
        {options.map(rating => (
          <div key={rating.name} className={styles.ratingOption}>
            <button
              type="button"
              role="radio"
              aria-label={rating.name}
              aria-checked={selectedRating === rating.value}
              onClick={() => handleRatingSelect(rating.value)}
              onFocus={event => handleRatingFocus(rating.value, event)}
              className={clsx(styles.ratingButton, colorStyles[rating.color])}
            >
              {rating.icon}
            </button>
            <span className={styles.ratingCaption}>{rating.name}</span>
          </div>
        ))}
      </div>
      <div className={styles.messageContainer}>
        <FormControl>
          <FormControl.Label>Message</FormControl.Label>
          <Textarea
            placeholder={placeholderText}
            value={feedbackText}
            onChange={handleFeedbackChange}
            resize="vertical"
            block
            rows={5}
          />
          <FormControl.Caption className={styles.privacyText}>
            Please don&apos;t include sensitive, confidential, or personal data. Your feedback helps us improve our
            services in line with our{' '}
            <Link
              href="https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement"
              inline
              muted
            >
              Privacy Policy
            </Link>
            .
          </FormControl.Caption>
        </FormControl>

        <div className={styles.validationError} role="alert" key={errorKey}>
          {errors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderFooter = () => (
    <div className={styles.footerContainer}>
      <div>
        {requestID && (
          <Tooltip text="Copy logs to add in feedback" direction="n">
            <Button
              leadingVisual={
                copiedLogs ? <CheckIcon className={styles.copyLogsSuccessIcon} size={16} /> : <CopyIcon size={16} />
              }
              onClick={handleCopyLogs}
              variant="default"
            >
              {copiedLogs ? 'Copied!' : 'Copy logs'}
            </Button>
          </Tooltip>
        )}
      </div>
      <div className={styles.footerActionButtonGroup}>
        <Button onClick={handleCancelClick}>Cancel</Button>
        <Button variant="primary" type="submit" onClick={handleSubmit}>
          Send
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {isOpen && (
        <Dialog
          onClose={handleDialogClose}
          title={title}
          renderBody={renderBody}
          renderFooter={renderFooter}
          className={styles.Dialog}
          returnFocusRef={returnFocusRef}
        />
      )}
    </>
  )
}

export const thumbsDownIcon = <ThumbsdownIcon className={styles.thumbsDownIcon} />
export const thumbsUpIcon = <ThumbsupIcon className={styles.thumbsUpIcon} />

export const frustratedIcon = <SmileyFrustratedIcon />
export const frownIcon = <SmileyFrownIcon />
export const neutralIcon = <SmileyNeutralIcon />
export const grinIcon = <SmileyGrinIcon />
export const smileIcon = <SmileyIcon />
